import { addTool, StackScrollTool, PlanarRotateTool, TrackballRotateTool, ToolGroupManager, PanTool, ZoomTool, WindowLevelTool, ArrowAnnotateTool, ProbeTool, LengthTool, AngleTool, RectangleROITool, EllipticalROITool, CrosshairsTool, Enums as ToolsEnums } from "@cornerstonejs/tools";
import { ContrastTool } from "../tools/ContrastTool";
// ================= REGISTER TOOLS =================
addTool(PanTool);
addTool(ZoomTool);
addTool(WindowLevelTool);
addTool(StackScrollTool);
addTool(ArrowAnnotateTool);
addTool(ProbeTool);
addTool(LengthTool);
addTool(AngleTool);
addTool(RectangleROITool);
addTool(EllipticalROITool);
addTool(PlanarRotateTool);
addTool(ContrastTool);
addTool(CrosshairsTool);

function isExtentError(error) {
  const message = String(error?.message || error || "");
  return (
    message.includes("extent") ||
    message.includes("iterable") ||
    message.includes("undefined") ||
    message.includes("length")
  );
}

function wrapCrosshairsMethod(target, name) {
  if (!target || typeof target[name] !== "function" || target[name].__pacsWrapped) {
    return;
  }

  const original = target[name];
  const wrapped = function (...args) {
    try {
      return original.apply(this, args);
    } catch (error) {
      if (isExtentError(error)) {
        return;
      }
      throw error;
    }
  };
  wrapped.__pacsWrapped = true;
  target[name] = wrapped;
}

function shieldCrosshairs(instance) {
  [
    "resetCrosshairs",
    "onCameraReset",
    "onResetCamera",
    "mouseMoveCallback",
    "_getWorldPos",
    "computeToolData",
  ].forEach((name) => wrapCrosshairsMethod(instance, name));
}

try {
  shieldCrosshairs(CrosshairsTool.prototype);
} catch {
  // Prototype patch is best-effort.
}

// ================= TOOL GROUPS =================
const STACK_TOOL_GROUP_ID = "STACK_TOOL_GROUP";
const MPR_TOOL_GROUP_ID = "MPR_TOOL_GROUP";

// Create or get Stack tool group (for non-MPR viewports)
export function getStackToolGroup() {
  let toolGroup = ToolGroupManager.getToolGroup(STACK_TOOL_GROUP_ID);
  
  if (!toolGroup) {
    toolGroup = ToolGroupManager.createToolGroup(STACK_TOOL_GROUP_ID);
    toolGroup.addTool(PanTool.toolName);
    toolGroup.addTool(ZoomTool.toolName);
    toolGroup.addTool(WindowLevelTool.toolName);
    toolGroup.addTool(StackScrollTool.toolName);
    toolGroup.addTool(ArrowAnnotateTool.toolName);
    toolGroup.addTool(ProbeTool.toolName);
    toolGroup.addTool(PlanarRotateTool.toolName);
    toolGroup.addTool(LengthTool.toolName);
    toolGroup.addTool(AngleTool.toolName);
    toolGroup.addTool(RectangleROITool.toolName);
    toolGroup.addTool(EllipticalROITool.toolName);
    toolGroup.addTool(ContrastTool.toolName);
  }
  
  return toolGroup;
}

export function getMPRToolGroup() {
  let toolGroup = ToolGroupManager.getToolGroup(MPR_TOOL_GROUP_ID);
  
  if (!toolGroup) {
    toolGroup = ToolGroupManager.createToolGroup(MPR_TOOL_GROUP_ID);
    
    toolGroup.addTool(PanTool.toolName);
    toolGroup.addTool(ZoomTool.toolName);
    toolGroup.addTool(WindowLevelTool.toolName);
    toolGroup.addTool(StackScrollTool.toolName);
    toolGroup.addTool(ArrowAnnotateTool.toolName);
    toolGroup.addTool(ProbeTool.toolName);
    toolGroup.addTool(PlanarRotateTool.toolName);
    toolGroup.addTool(LengthTool.toolName);
    toolGroup.addTool(AngleTool.toolName);
    toolGroup.addTool(RectangleROITool.toolName);
    toolGroup.addTool(EllipticalROITool.toolName);
    toolGroup.addTool(ContrastTool.toolName);
    toolGroup.addTool(CrosshairsTool.toolName);
    try {
      const crosshairsInstance = toolGroup.getToolInstance(CrosshairsTool.toolName);
      if (crosshairsInstance) {
        shieldCrosshairs(crosshairsInstance);
      }
    } catch (patchErr) {
      console.error("Failed to inject crosshairs crash patch:", patchErr);
    }
  }
  
  return toolGroup;
}

export function setCrosshairsEnabled(enabled) {
  const toolGroup = getMPRToolGroup();
  if (!toolGroup) return;

  try {
    if (enabled) {
      toolGroup.setToolActive(CrosshairsTool.toolName, {
        bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
      });
    } else {
      toolGroup.setToolDisabled(CrosshairsTool.toolName);
    }
  } catch {
    try {
      toolGroup.setToolPassive(CrosshairsTool.toolName);
    } catch {
      // Crosshairs may not exist yet.
    }
  }
}

export function createToolGroup(viewportId, renderingEngineId, isMPR) {
  const targetGroup = isMPR
    ? getMPRToolGroup()
    : getStackToolGroup();

  ToolGroupManager.getAllToolGroups().forEach(group => {
    if (group.id !== targetGroup.id) {
      try {
      
      } catch (e) {}
    }
  });
  const alreadyAdded =
    targetGroup.viewportsInfo?.some?.(
      vp =>
        vp.viewportId === viewportId &&
        vp.renderingEngineId === renderingEngineId
    );

  if (!alreadyAdded) {
    targetGroup.addViewport(viewportId, renderingEngineId);
  }

  return targetGroup;
}

export function removeViewportFromToolGroups(viewportId) {
  const stackGroup = ToolGroupManager.getToolGroup(STACK_TOOL_GROUP_ID);
  const mprGroup = ToolGroupManager.getToolGroup(MPR_TOOL_GROUP_ID);

  if (stackGroup) {
    try {
      stackGroup.removeViewport(viewportId);
    } catch (e) {}
  }
  if (mprGroup) {
    try {
      mprGroup.removeViewport(viewportId);
    } catch (e) {}
  }
}